// ----------------------------------------------------------------------
// <copyright file="Izenda.WorkspaceStore.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------
!(function() {
  const {
    filter,
    isEmpty,
    isNil,
    findIndex,
    concat,
    toUpper,
    head,
    escapeRegExp,
    create,
    find,
    sortBy
  } = require('lodash/fp');

  var m = require('mobx');
  var dataFetcher = require('fetchers/Izenda.WorkspaceDataFetcher');
  var tenantFetcher = require('fetchers/Izenda.TenantManagementDataFetcher');
  var systemFetcher = require('fetchers/Izenda.SystemSettingFetcher');
  var constants = require('logic/data/Izenda.CopyManagementConstants');
  var Workspaces = require('./models/Izenda.Workspaces');

  function WorkspaceStore() {
    var observable = {
      // A filtered workspace list that matches the filter string
      filteredWorkspaces: function() {
        var filterRegex = new RegExp(escapeRegExp(this.filter), 'i');
        var match = function(item) {
          return filterRegex.test(item.name);
        };
        return sortBy(function(item) {
          return toUpper(item.name);
        }, this.list.filter(match));
      },
      // available sources for Source Dropdown: only display System & tenants
      // which not available in destination tenants
      availableSources: function() {
        var selectedDestinationTenantIDs = this.item.selectedDestinationTenantIDs;
        var selectedSource = this.item.isSystemSource ? [] : find({ id: this.item.sourceId }, this.tenantList);

        return isNil(selectedDestinationTenantIDs)
          ? this.tenantList
          : this.item.isSelectAllTenants
            ? concat(constants.systemSource, selectedSource)
            : filter(function(item) {
                return selectedDestinationTenantIDs.indexOf(item.id) === -1;
              }, this.tenantList);
      }
    };

    Workspaces.call(observable);

    // Extend the store from workspaces model and make it observable
    m.extendObservable(this, observable);

    this.dispose = m.observe(
      this.list,
      function() {
        // Move current workspace to next eligible workspace in the list
        // whenever the list changed.
        this.nextWorkspace(this.item ? this.item.id : null);
      }.bind(this)
    );
  }

  WorkspaceStore.prototype = create(Workspaces.prototype, {
    constructor: WorkspaceStore
  });

  /**
   * Move to next eligible workspace in showing list.
   */
  WorkspaceStore.prototype.nextWorkspace = function(startId) {
    if ((isNil(this.item) || isNil(this.item.id)) && isNil(startId)) {
      if (isEmpty(this.list)) {
        // Case of intial loading the empty list, a new workspace detail is active
        // in order to allow adding new workspace.
        this.addWorkspace();
      } else {
        // Case of first loading (active item is undefined), move to first workspace
        var first = head(this.list);
        this.item = first;
      }
    } else if (this.item.id !== startId) {
      var next = find(['id', startId], this.filteredWorkspaces) || find(['id', startId], this.list);
      if (isNil(next)) {
        this.addWorkspace();
      } else {
        this.item = next;
      }
    } else if (this.tenantId) {
      // In case of when change tenant then get the first value of list workspace string
      var firstItem = head(this.list);
      isNil(firstItem) ? this.addWorkspace() : (this.item = firstItem);
    }
  };

  /**
   * Load all workspaces and supported db types from API
   * @return {Promise} A promise of loading workspace list
   */
  WorkspaceStore.prototype.loadList = m.action(function() {
    var store = this;
    var showOnlyMyWorkspace = store.showOnlyMyWorkspace;

    return Promise.join(
      dataFetcher.getAllWorkspaces(showOnlyMyWorkspace),
      tenantFetcher.getAllNameTenantList(),
      systemFetcher.getAllUsers(),
      function(workspaceList, tenantList, userList) {
        // Defer notifying changes until reading finished
        store.readTenantList(tenantList);
        store.readWorkspaceList(workspaceList);
        store.updateOwnerList(userList);
        return null;
      }
    ).then(function() {
      if (isEmpty(store.list)) {
        store.addWorkspace();
      }
    });
  });

  /**
   * Delete a workspace. Side effect of deletion is moving selected item
   * in the list to next one.
   * @param  {Object} delItem Deleted workspace
   * @param  {Promise} A promise of deletion
   */
  WorkspaceStore.prototype.deleteWorkspace = m.action(function(delItem) {
    // Find next available Workspace in the list by round-robin ordering pattern
    var findNextId = function(availableList, fullList, current) {
      function nextEligible(list) {
        if (delItem.id === current.id && list.length > 1) {
          var index = findIndex(['id', delItem.id], list);
          // If deleted item is the last item, then take first one, but the next one.
          return index === list.length - 1 ? list[0].id : list[index + 1].id;
        } else if (delItem.id !== current.id) {
          return current.id;
        }
        return null;
      }

      return nextEligible(availableList) || nextEligible(fullList);
    };

    return dataFetcher.delete(delItem.id).then(
      function() {
        var store = this;
        // Select next item in filtered Workspace list
        var nextId = findNextId(store.filteredWorkspaces, store.list, store.item);
        store.nextWorkspace(nextId);

        // Delete Workspace detail instance in the list
        var idx = findIndex(['id', delItem.id], store.list);
        store.list.splice(idx, 1);
        return null;
      }.bind(this)
    );
  });

  module.exports = WorkspaceStore;
})();



// WEBPACK FOOTER //
// ./ui/logic/Izenda.WorkspaceStore.js